import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ContactSection from '../components/ContactSection';

const allPartners = [
    { name: 'Cisco',            logo: require('../assets/images/partner/Cisco.png') },
    { name: 'Dell',             logo: require('../assets/images/partner/Dell_Logo.png') },
    { name: 'HPE',              logo: require('../assets/images/partner/Hewlett_Packard_Enterprise_logo.svg.png') },
    { name: 'Microsoft',        logo: require('../assets/images/partner/Microsoft.png') },
    { name: 'IBM',              logo: require('../assets/images/partner/ibm.webp') },
    { name: 'Lenovo',           logo: require('../assets/images/partner/lonovo.png') },
    { name: 'Oracle',           logo: require('../assets/images/partner/Oracle.png') },
    { name: 'Salesforce',       logo: require('../assets/images/partner/Salesforce.png') },
    { name: 'AWS',              logo: require('../assets/images/partner/Amazon Web Services.png') },
    { name: 'VMware',           logo: require('../assets/images/partner/vmware.jpeg') },
    { name: 'Red Hat',          logo: require('../assets/images/partner/redhat.jpeg') },
    { name: 'Barracuda',        logo: require('../assets/images/partner/Barracuda.png') },
    { name: 'Juniper Networks', logo: require('../assets/images/partner/Juniper Networks.png') },
    { name: 'SonicWall',        logo: require('../assets/images/partner/SonicWall.webp') },
    { name: 'Adobe',            logo: require('../assets/images/partner/Adobe.png') },
    { name: 'AutoCAD',          logo: require('../assets/images/partner/AutoCAD.png') },
    { name: 'Acronis',          logo: require('../assets/images/partner/Acronis.png') },
    { name: 'Linux',            logo: require('../assets/images/partner/linux.jpeg') },
    { name: 'SUSE',             logo: require('../assets/images/partner/suse.jpeg') },
    { name: 'Citrix',           logo: require('../assets/images/partner/citrix.jpeg') },
    { name: 'Super Micro',      logo: require('../assets/images/partner/Super Micro.jpg') },
    { name: 'Yotta',            logo: require('../assets/images/partner/Yotta.jpg') },
    { name: 'Parent',           logo: require('../assets/images/partner/Parent-logo.webp') },
];

const advantages = [
    { title: 'Shared Vision for Growth', body: 'We align partnership objectives with long-term business and market expansion goals, ensuring both parties move forward together.' },
    { title: 'Integrated Technical Expertise', body: 'Combining partner certifications and Solaris engineering depth to design and deliver solutions that exceed client expectations.' },
    { title: 'Credibility and Market Reach', body: 'Backed by globally recognised vendor alliances, we carry credibility that accelerates client confidence and market penetration.' },
    { title: 'Innovation Through Collaboration', body: 'Joint innovation initiatives and co-development efforts keep our service portfolio at the cutting edge of technology.' },
    { title: 'Commitment to Delivery Excellence', body: 'Rigorous quality benchmarks and shared accountability frameworks ensure consistent, high-quality outcomes on every engagement.' },
];

export default function Partners() {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    const [activeAdvantage, setActiveAdvantage] = useState(0);

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '400px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden',
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.78)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none',
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '140px 15px 100px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px',
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Strategy
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 20px', maxWidth: '780px',
                        }}>
                            Building Stronger Futures Through Strategic Partnerships
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.68)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '520px', margin: '0 auto',
                        }}>
                            Combining certified technical excellence with industry-leading vendor alliances to deliver IT solutions that scale with your ambition.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── STRATEGY OVERVIEW ── */}
            <section style={{ padding: '100px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }} viewport={{ once: true }}
                                style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.12)' }}
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&auto=format&fit=crop&q=80"
                                    alt="Strategic partnerships"
                                    style={{ width: '100%', display: 'block', objectFit: 'cover', minHeight: '340px' }}
                                />
                            </motion.div>
                        </div>
                        <div className="col-lg-7">
                            <motion.div
                                initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }}
                            >
                                <div style={{
                                    background: '#f8fafc', borderRadius: '20px',
                                    padding: '44px 40px', border: '1px solid #e2e8f0',
                                }}>
                                    <p style={{ color: '#2563eb', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.76rem', marginBottom: '14px' }}>STRATEGY</p>
                                    <h2 style={{ color: '#0f172a', fontWeight: 800, fontSize: 'clamp(1.7rem, 2.8vw, 2.4rem)', lineHeight: 1.2, marginBottom: '20px' }}>
                                        Our Partnership Philosophy
                                    </h2>
                                    <p style={{ color: '#475569', fontSize: '0.97rem', lineHeight: 1.85, marginBottom: '20px' }}>
                                        At Solaris, partnerships are central to how we deliver value. By working closely with global technology providers, platform leaders, and solution innovators, we combine deep technical expertise with industry-aligned best practices.
                                    </p>
                                    <p style={{ color: '#475569', fontSize: '0.97rem', lineHeight: 1.85, marginBottom: '36px' }}>
                                        Our partner ecosystem enables us to design, deploy, and manage high-performance IT environments that meet evolving business, security, and compliance requirements - at scale, and with confidence.
                                    </p>
                                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                                        <button style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                            color: '#fff', border: 'none',
                                            padding: '13px 8px 13px 26px',
                                            borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                            cursor: 'pointer', boxShadow: '0 6px 20px rgba(37,99,235,0.35)',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                        }}
                                            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.5)'; }}
                                            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.35)'; }}
                                        >
                                            <span style={{ marginRight: '14px' }}>Join Our Partner Ecosystem</span>
                                            <span style={{
                                                background: '#fff', borderRadius: '50%', width: '34px', height: '34px',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                        </button>
                                    </Link>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── TECHNOLOGY PARTNER ECOSYSTEM ── */}
            <section style={{ padding: '90px 0 0', background: '#f8fafc', overflow: 'hidden' }}>
                <div className="container">
                    <div className="row align-items-start g-4 mb-5">
                        <div className="col-lg-6">
                            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                                <p style={{ color: '#2563eb', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.76rem', marginBottom: '14px' }}>ECOSYSTEM</p>
                                <h2 style={{ color: '#0f172a', fontWeight: 800, fontSize: 'clamp(2rem, 3.5vw, 3rem)', lineHeight: 1.2, margin: 0 }}>
                                    Our Technology<br />Partner Ecosystem
                                </h2>
                            </motion.div>
                        </div>
                        <div className="col-lg-6 d-flex align-items-end">
                            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }}
                                style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.8, margin: 0, paddingBottom: '6px' }}>
                                An integrated network of global and regional technology partners supporting every layer of enterprise IT — from infrastructure to security.
                            </motion.p>
                        </div>
                    </div>
                </div>

                {/* ── Infinite Marquee Row 1 (left→right) ── */}
                <div style={{ position: 'relative', overflow: 'hidden', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', width: 'max-content', animation: 'marquee-left 35s linear infinite' }}>
                        {[...allPartners, ...allPartners].map((p, i) => (
                            <div key={i} style={{
                                flexShrink: 0, margin: '0 12px',
                                background: '#fff', borderRadius: '14px',
                                border: '1.5px solid #e2e8f0',
                                width: '160px', height: '90px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: '14px 18px',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                            }}>
                                <img src={p.logo} alt={p.name}
                                    style={{ maxWidth: '110px', maxHeight: '50px', objectFit: 'contain' }} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Infinite Marquee Row 2 (right→left) ── */}
                <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '72px' }}>
                    <div style={{ display: 'flex', width: 'max-content', animation: 'marquee-right 40s linear infinite' }}>
                        {[...allPartners.slice().reverse(), ...allPartners.slice().reverse()].map((p, i) => (
                            <div key={i} style={{
                                flexShrink: 0, margin: '0 12px',
                                background: '#fff', borderRadius: '14px',
                                border: '1.5px solid #e2e8f0',
                                width: '160px', height: '90px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: '14px 18px',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                            }}>
                                <img src={p.logo} alt={p.name}
                                    style={{ maxWidth: '110px', maxHeight: '50px', objectFit: 'contain' }} />
                            </div>
                        ))}
                    </div>
                </div>

                <style>{`
                    @keyframes marquee-left {
                        0%   { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    @keyframes marquee-right {
                        0%   { transform: translateX(-50%); }
                        100% { transform: translateX(0); }
                    }
                `}</style>
            </section>

            {/* ── ALL PARTNERS GRID ── */}
            <section style={{ padding: '80px 0', background: '#fff' }}>
                <div className="container">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
                        style={{ textAlign: 'center', marginBottom: '52px' }}>
                        <p style={{ color: '#2563eb', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.76rem', marginBottom: '12px' }}>OUR PARTNERS</p>
                        <h2 style={{ color: '#0f172a', fontWeight: 800, fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', lineHeight: 1.2, marginBottom: '14px' }}>
                            Trusted by Industry Leaders
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1rem', maxWidth: '500px', margin: '0 auto', lineHeight: 1.75 }}>
                            We partner with the world's leading technology vendors to deliver best-in-class enterprise solutions.
                        </p>
                    </motion.div>

                    <div className="row g-3">
                        {allPartners.map((partner, i) => (
                            <div key={i} className="col-lg-2 col-md-3 col-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35, delay: i * 0.03 }}
                                    viewport={{ once: true }}
                                    style={{
                                        background: '#fff', borderRadius: '14px',
                                        border: '1.5px solid #e2e8f0',
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        padding: '20px 14px', minHeight: '100px',
                                        transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                                        cursor: 'default',
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(37,99,235,0.12)'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                                    onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <img src={partner.logo} alt={partner.name}
                                        style={{ maxWidth: '90px', maxHeight: '44px', objectFit: 'contain', marginBottom: '10px' }} />
                                    <span style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', letterSpacing: '0.5px' }}>
                                        {partner.name}
                                    </span>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── ACCREDITATION (dark bg with overlay image) ── */}
            <section style={{
                position: 'relative', overflow: 'hidden',
                backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                padding: '100px 0',
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.82)' }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 16px', marginBottom: '24px',
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>ACCREDITATION</span>
                        </div>
                        <h2 style={{
                            color: '#fff', fontWeight: 700,
                            fontSize: 'clamp(2rem, 4vw, 3rem)',
                            lineHeight: 1.2, marginBottom: '60px',
                        }}>
                            Certified expertise backed<br />by global standards
                        </h2>
                    </motion.div>

                    <div className="row g-4">
                        {[
                            {
                                logo: 'CISCO',
                                logoColor: '#00bceb',
                                subtitle: 'Cisco Global Partner',
                                body: 'Delivering across India, the US, Dubai, and Singapore with certified Cisco engineers and advanced solution specialisations.',
                                delay: 0,
                            },
                            {
                                logo: 'FORTINET',
                                logoColor: '#ee3124',
                                subtitle: 'Fortinet Advanced Partner',
                                body: 'Enterprise Security, Elevated. Our Fortinet partnership brings next-generation firewall, SD-WAN, and unified threat management capabilities.',
                                delay: 0.1,
                            },
                            {
                                logo: 'CISCO PREFERRED',
                                logoColor: '#00bceb',
                                subtitle: 'Certified Cisco Preferred Partner',
                                body: 'Recognised for consistent excellence in delivery, customer satisfaction, and technical competency across multiple Cisco solution areas.',
                                delay: 0.2,
                            },
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: item.delay }} viewport={{ once: true }}
                                    style={{
                                        background: 'rgba(255,255,255,0.07)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        borderRadius: '20px', padding: '36px 32px',
                                        height: '100%',
                                    }}
                                >
                                    <div style={{
                                        fontSize: '1.5rem', fontWeight: 900, letterSpacing: '2px',
                                        color: item.logoColor, marginBottom: '16px',
                                        fontFamily: 'system-ui, sans-serif',
                                    }}>
                                        {item.logo}
                                    </div>
                                    <p style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', marginBottom: '10px' }}>
                                        {item.subtitle}
                                    </p>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
                                        {item.body}
                                    </p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── A PARTNERSHIP THAT WORKS BOTH WAYS ── */}
            <section style={{ padding: '100px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-start g-4 mb-5">
                        <div className="col-lg-6">
                            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                                <p style={{ color: '#2563eb', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.76rem', marginBottom: '14px' }}>ADVANTAGE</p>
                                <h2 style={{ color: '#0f172a', fontWeight: 800, fontSize: 'clamp(2rem, 3.5vw, 3rem)', lineHeight: 1.2, margin: 0 }}>
                                    A Partnership That<br />Works Both Ways
                                </h2>
                            </motion.div>
                        </div>
                        <div className="col-lg-6 d-flex align-items-end">
                            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }}
                                style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.8, margin: 0, paddingBottom: '6px' }}>
                                A partnership model built on performance, transparency, and long-term value - where both parties grow stronger together.
                            </motion.p>
                        </div>
                    </div>

                    <div className="row g-5 align-items-center">
                        {/* Accordion */}
                        <div className="col-lg-6">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {advantages.map((adv, i) => (
                                    <motion.div key={i}
                                        initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: i * 0.06 }} viewport={{ once: true }}
                                    >
                                        <button
                                            onClick={() => setActiveAdvantage(activeAdvantage === i ? -1 : i)}
                                            style={{
                                                width: '100%', textAlign: 'left',
                                                background: 'none', border: 'none',
                                                padding: '18px 0',
                                                borderBottom: '1px solid #f1f5f9',
                                                cursor: 'pointer',
                                                display: 'flex', alignItems: 'flex-start', gap: '14px',
                                            }}
                                        >
                                            <span style={{
                                                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginTop: '8px',
                                                background: activeAdvantage === i ? '#2563eb' : '#cbd5e1',
                                                transition: 'background 0.2s',
                                            }} />
                                            <div>
                                                <p style={{
                                                    color: activeAdvantage === i ? '#0f172a' : '#64748b',
                                                    fontWeight: activeAdvantage === i ? 700 : 500,
                                                    fontSize: '1rem', margin: 0, transition: 'color 0.2s',
                                                }}>{adv.title}</p>
                                                {activeAdvantage === i && (
                                                    <motion.p
                                                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                                                        style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.8, margin: '10px 0 0' }}
                                                    >
                                                        {adv.body}
                                                    </motion.p>
                                                )}
                                            </div>
                                        </button>
                                    </motion.div>
                                ))}
                            </div>
                            <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.85, marginTop: '32px' }}>
                                We align partnership objectives with long-term business and market expansion goals. Our collaborative approach ensures mutual success and sustained value creation across every engagement.
                            </p>
                        </div>

                        {/* Image */}
                        <div className="col-lg-6">
                            <motion.div
                                initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }} viewport={{ once: true }}
                                style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.10)' }}
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&auto=format&fit=crop&q=80"
                                    alt="Partnership"
                                    style={{ width: '100%', height: '440px', objectFit: 'cover', display: 'block' }}
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── STATS ── */}
            <section style={{ padding: '80px 0', background: '#0f172a' }}>
                <div className="container">
                    <div className="row g-4 text-center">
                        {[
                            { value: '50+', label: 'Technology Partners' },
                            { value: '15+', label: 'Years of Partnerships' },
                            { value: '200+', label: 'Joint Deployments' },
                            { value: '4', label: 'Global Regions' },
                        ].map((stat, i) => (
                            <div key={i} className="col-lg-3 col-6">
                                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.08 }} viewport={{ once: true }}>
                                    <div style={{ fontSize: 'clamp(2.4rem, 5vw, 3.5rem)', fontWeight: 900, color: '#fff', lineHeight: 1 }}>{stat.value}</div>
                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginTop: '8px', fontWeight: 500 }}>{stat.label}</div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
}
